import { useEffect, useState } from 'react';
import { VisitorView } from 'src/editor/insights/constants';
import { useDepsMatchingCallbackRef } from 'src/utils/react/deps-matching-callback-ref.hook';
import { buildInsightsViewDetails } from '../helpers';
import { apiGetJourneyInsights, apiGetJourneyNativeForms } from 'src/utils/journeyApi';
import { TimePeriodParam } from '../constants';

type Params = {
  journeyUuid?: string;
  page: number;
  preventImmediateFetching?: boolean;
  filters?: {
    email?: string;
    node_ids?: number[];
    alias_ids?: number[];
    session_uuid?: string;
    time_period?: TimePeriodParam;
    event_types?: string[];
    all?: boolean;
  };
};

export function useJourneyInsights({ journeyUuid, page, filters, preventImmediateFetching = false }: Params) {
  const [fetching, setFetching] = useState(true);
  const [visitorViews, setVisitorViews] = useState<VisitorView[]>([]);
  const [nativeForms, setNativeForms] = useState<any[]>([]);
  const [pages, setPages] = useState(1);
  const [showPagination, setShowPagination] = useState(false);

  const onFetchJourneyInsightsRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setVisitorViews(response.views.map(buildInsightsViewDetails));
      setPages(response.pages);
      setFetching(false);
      setShowPagination(true);
    },
    [journeyUuid, page, filters]
  );

  const onFetchNativeFormsRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setNativeForms(response.forms);
      setFetching(false);
      setShowPagination(false);
    },
    [journeyUuid, filters]
  );

  const fetchJourneyInsights = async () => {
    setFetching(true);
    if (journeyUuid) {
      const { email, session_uuid, node_ids, alias_ids, time_period, event_types, all } = filters || {};

      const response = await apiGetJourneyInsights(journeyUuid, page, {
        ...(email && { email }),
        ...(session_uuid && { session_uuid }),
        ...(node_ids && node_ids.length > 0 && { node_ids: node_ids.join(',') }),
        ...(alias_ids && alias_ids.length > 0 && { alias_ids: alias_ids.join(',') }),
        ...(time_period && { time_period }),
        ...(event_types && event_types.length > 0 && { event_types: event_types.join(',') }),
        all,
      });
      onFetchJourneyInsightsRef.current && onFetchJourneyInsightsRef.current(response, [journeyUuid, page, filters]);
      return response;
    }
  };

  const fetchJourneyNativeForms = async () => {
    setFetching(true);
    if (journeyUuid) {
      const { email, session_uuid, node_ids, alias_ids, time_period, event_types, all } = filters || {};
      const response = await apiGetJourneyNativeForms(journeyUuid, {
        ...(email && { email }),
        ...(session_uuid && { session_uuid }),
        ...(node_ids && node_ids.length > 0 && { node_ids: node_ids.join(',') }),
        ...(alias_ids && alias_ids.length > 0 && { alias_ids: alias_ids.join(',') }),
        ...(time_period && { time_period }),
        ...(event_types && event_types.length > 0 && { event_types: event_types.join(',') }),
        all,
      });
      onFetchNativeFormsRef.current && onFetchNativeFormsRef.current(response, [journeyUuid, page, filters]);
      return response;
    }
  };

  useEffect(() => {
    if (!preventImmediateFetching) {
      fetchJourneyInsights();
    }
  }, [preventImmediateFetching, journeyUuid, page, filters]);

  return { fetching, visitorViews, pages, showPagination, fetchJourneyInsights, fetchJourneyNativeForms };
}
